// Page module: the spectator follow view.
//
// This page is a READER OF THE PUBLIC PLAY-BY-PLAY FEED, exactly like kzs.si —
// same JSON, same five rules, same resync. See docs/api/play-by-play.md for the
// wire format and docs/adr/0002-public-play-by-play-feed.md for why.
//
// That is the point of doing it this way rather than having the server push
// finished HTML: the protocol we export is the one we look at every match day,
// so a hole in it shows up here first. The header and the play-by-play column
// therefore have exactly one implementation, and it is this one.
//
// The scoresheet facsimile is the one thing the feed cannot carry — it is the
// whole printed document. The server renders it, and this page fetches it again
// when the feed says the scoresheet moved. Pulled, not pushed: the keeper's save
// no longer renders a single template for anybody's benefit.

global.followMatch = {
  // how many rows the column shows; the note under them says when it is capping
  PLAYS_SHOWN: 50,
  // one fetch of the (heavy) sheet at a time, and never twice for one burst of saves
  SHEET_DEBOUNCE_MS: 700,
  // Prematch has no events, so the feed says nothing while the line-ups fill in.
  // A slow poll keeps the sheet column alive until the ball goes up.
  PREMATCH_POLL_MS: 20000,

  data: {},

  init: function(options) {
    var d = followMatch.data;
    d.options = options;
    d.events = {};       // id -> event
    d.order = [];        // authoritative; ids do not sort, never append blindly
    d.revision = -1;
    d.state = null;
    // Which revision the server-rendered first paint of the sheet is at, so the
    // page does not immediately fetch back the document it was just handed.
    d.sheetRevision = options.sheetRevision;

    // First paint over HTTP, so the page is complete before — and even without —
    // a websocket. The socket only has to carry what happens next.
    followMatch.load();
    d.subscription = ws.subscribe('PlayByPlayChannel', { matchId: options.matchId }, followMatch.apply);
    d.prematchPoll = setInterval(followMatch.pollWhilePrematch, followMatch.PREMATCH_POLL_MS);
  },

  // cache: false — the endpoint carries an ETag so that a POLLER is cheap, but
  // this page only asks when it needs the truth: once at load, and again when a
  // message arrived with a hole in it. A cached answer to that second question
  // is exactly the wrong answer.
  load: function() {
    var d = followMatch.data;
    if(d.loading) return;

    d.loading = true;
    $.ajax({ url: d.options.feedUrl, dataType: 'json', cache: false })
      .done(followMatch.apply)
      .always(function() { d.loading = false; });
  },

  // The five rules from docs/api/play-by-play.md, in order. Nothing is committed
  // until the message is known to be complete, so a gap leaves the page showing
  // what it had rather than half of what comes next.
  apply: function(message) {
    var d = followMatch.data;
    if(!message || message.revision <= d.revision) return;      // 1

    var events, order = message.order || [];                    // 2
    if(message.type === 'snapshot') {                           // 4
      events = $.extend({}, message.events);
    } else {
      events = $.extend({}, d.events, message.events);
      (message.removed || []).forEach(function(r) { delete events[r.id]; });
    }

    if(order.some(function(id) { return !events[id]; })) {      // 3
      // An id we have never been sent the content of: a message was missed.
      // Asked over HTTP rather than with the channel's own resync action —
      // the socket is the thing that has just proved unreliable, and a request
      // that stands on its own cannot be lost the same way twice. (The resync
      // action is there for readers who prefer it; see docs/api.)
      //
      // d.revision is deliberately NOT advanced, so the snapshot that answers
      // this is not mistaken for something we have already applied.
      followMatch.load();
      return;
    }

    d.revision = message.revision;
    d.events = events;
    d.order = order;
    d.state = message;

    followMatch.render();
    followMatch.refreshSheet();
  },

  render: function() {
    followMatch.renderHead();
    followMatch.renderPlays();
    followMatch.renderTeams();
  },

  // The two roster cards under the sheet: everyone on the sheet in jersey
  // order, with the points they scored and their fouls as DOTS — the letters
  // (I, FL, DI …) belong to the printed sheet, a spectator counts dots. Both
  // numbers are aggregated from the same feed the plays column reads, so a
  // correction or an undo fixes them the same way it fixes the score. Bench
  // fouls stay off these rows on their own: bench events carry no player.
  renderTeams: function() {
    var roots = { home: document.getElementById('follow-team-home'),
                  away: document.getElementById('follow-team-away') };
    if(!roots.home || !roots.away) return;
    var shell = roots.home.parentNode;
    var d = followMatch.data;
    var s = d.state || {};
    var lineups = s.lineups || {};
    if(!s.started || ((lineups.home || []).length + (lineups.away || []).length) === 0) {
      roots.home.innerHTML = '';
      roots.away.innerHTML = '';
      if(shell) shell.classList.remove('has-teams');   // the side columns collapse
      return;
    }
    if(shell) shell.classList.add('has-teams');

    var stats = {};   // player id -> { points, fouls }
    d.order.forEach(function(id) {
      var e = d.events[id];
      if(!e || e.status !== 'active' || !e.player || !e.player.id) return;
      var st = stats[e.player.id] || (stats[e.player.id] = { points: 0, fouls: 0 });
      if(e.kind === 'basket') st.points += (e.points || 0);
      else if(e.kind === 'free_throw_made') st.points += 1;
      else if(e.kind.indexOf('foul') === 0) st.fouls += 1;
    });

    ['home', 'away'].forEach(function(side) {
      var root = roots[side];
      var players = (lineups[side] || []).slice().sort(function(a, b) { return Number(a.number) - Number(b.number); });
      if(players.length === 0) { root.innerHTML = ''; return; }

      var card = followMatch.el('section', 'fw-teamcard');
      var head = followMatch.el('header', 'fw-teamcard__head');
      head.appendChild(followMatch.jersey(((s.teams || {})[side] || {}).color));
      head.appendChild(followMatch.el('h2', 'fw-teamcard__name', ((s.teams || {})[side] || {}).name || ''));
      head.appendChild(followMatch.el('span', 'fw-teamcard__legend', 'napake · točke'));
      card.appendChild(head);

      var list = followMatch.el('ul', 'fw-teamcard__list');
      players.forEach(function(p) {
        var st = stats[p.id] || { points: 0, fouls: 0 };
        var row = followMatch.el('li', 'fw-tp');
        row.appendChild(followMatch.el('span', 'fw-tp__num', p.number));
        row.appendChild(followMatch.el('span', 'fw-tp__name', (p.firstName + ' ' + p.lastName).trim()));
        // five slots as on the sheet — anything past five stays five dots
        row.appendChild(followMatch.el('span', 'fw-tp__fouls', new Array(Math.min(st.fouls, 5) + 1).join('●')));
        row.appendChild(followMatch.el('span', 'fw-tp__pts', String(st.points)));
        list.appendChild(row);
      });
      card.appendChild(list);
      root.innerHTML = '';
      root.appendChild(card);
    });
  },

  // --- the sheet facsimile ---------------------------------------------------

  // Only when the scoresheet actually moved, and only one request in flight.
  refreshSheet: function() {
    var d = followMatch.data;
    if(d.state.revision === d.sheetRevision) return;

    d.sheetRevision = d.state.revision;
    clearTimeout(d.sheetTimer);
    d.sheetTimer = setTimeout(followMatch.fetchSheet, followMatch.SHEET_DEBOUNCE_MS);
  },

  fetchSheet: function() {
    var d = followMatch.data;
    if(d.sheetInFlight) return;

    d.sheetInFlight = true;
    $.get(d.options.sheetUrl)
      .done(function(html) { $('#follow-pdf').html(html); })
      .always(function() { d.sheetInFlight = false; });
  },

  // The line-ups fill in during prematch and produce no events at all, so
  // nothing would tell this page to look again.
  pollWhilePrematch: function() {
    var d = followMatch.data;
    if(!d.state) return;
    if(d.state.started || d.state.ended) {
      clearInterval(d.prematchPoll);
      return;
    }
    followMatch.fetchSheet();
  },

  // --- markup ---------------------------------------------------------------
  //
  // Built as nodes rather than strings: team and player names come from FMS and
  // are somebody else's data.

  el: function(tag, className, text) {
    var node = document.createElement(tag);
    if(className) node.className = className;
    if(text !== undefined && text !== null) node.appendChild(document.createTextNode(text));
    return node;
  },

  jersey: function(color, extraClass) {
    var swatch = followMatch.el('span', 'fw-jersey' + (extraClass ? ' ' + extraClass : ''));
    if(color) swatch.style.background = color;
    return swatch;
  },

  // "Konec tekme" / "V teku · 3. četrtina, 7'" / "Pred tekmo" — the same chip in
  // the header and above the play column.
  stateChip: function(withPeriod) {
    var s = followMatch.data.state;
    if(s.ended) return followMatch.el('span', 'fw-state fw-state--done', 'Konec tekme');
    if(!s.started) return followMatch.el('span', 'fw-state', 'Pred tekmo');

    var chip = followMatch.el('span', 'fw-state fw-state--live');
    chip.appendChild(followMatch.el('span', 'fw-state__dot'));
    var text = withPeriod ? 'V teku · ' + s.period.name + ', ' + s.period.minute + "'" : 'V živo';
    chip.appendChild(document.createTextNode(text));
    return chip;
  },

  renderHead: function() {
    var d = followMatch.data;
    var s = d.state;
    var pre = !s.started && !s.ended;

    var head = followMatch.el('div', 'fw-head');
    var teams = followMatch.el('div', 'fw-head__teams');

    var home = followMatch.el('span', 'fw-team');
    // the swatch appears once the live sheet owns the colours — before that they
    // are only a prematch default, not what the teams actually wear
    if(!pre) home.appendChild(followMatch.jersey(s.teams.home.color));
    home.appendChild(document.createTextNode(s.teams.home.name || ''));

    var away = followMatch.el('span', 'fw-team fw-team--away');
    away.appendChild(document.createTextNode(s.teams.away.name || ''));
    if(!pre) away.appendChild(followMatch.jersey(s.teams.away.color));

    teams.appendChild(home);
    teams.appendChild(followMatch.el('span', 'fw-score',
      pre ? '0 : 0' : s.score.home + ' : ' + s.score.away));
    teams.appendChild(away);

    var meta = followMatch.el('div', 'fw-head__meta');
    meta.appendChild(followMatch.stateChip(true));
    meta.appendChild(followMatch.el('span', 'fw-sep', '·'));
    meta.appendChild(followMatch.el('span', 'fw-head__facts', d.options.facts));
    meta.appendChild(followMatch.el('span', 'fw-head__spacer'));
    // the PDF is the frozen sheet — it exists only once a scoresheet is saved
    if(s.status === 'STARTED' || s.status === 'FINISHED') {
      var pdf = followMatch.el('a', 'fw-tool', 'Zapisnik PDF');
      pdf.href = d.options.pdfUrl;
      pdf.target = '_blank';
      meta.appendChild(pdf);
    }

    head.appendChild(teams);
    head.appendChild(meta);
    $('#follow-head').empty().append(head);
  },

  renderPlays: function() {
    var d = followMatch.data;
    var card = followMatch.el('div', 'follow-plays');
    var header = followMatch.el('header', 'fw-plays__head');
    header.appendChild(followMatch.el('p', 'fw-plays__title', 'Potek tekme'));
    header.appendChild(followMatch.stateChip(false));
    card.appendChild(header);

    if(!d.order.length) {
      card.appendChild(followMatch.el('p', 'fw-plays__note', d.state.started
        ? 'Ni še nobenih dogodkov.'
        : 'Ko se tekma začne, se bodo dogodki prikazovali tukaj — samodejno.'));
      $('#follow-logs').empty().append(card);
      return;
    }

    // newest first
    var newest = d.order.slice().reverse();
    var list = followMatch.el('ol', 'fw-plays__list');
    newest.slice(0, followMatch.PLAYS_SHOWN).forEach(function(id) {
      list.appendChild(followMatch.playRow(d.events[id]));
    });
    card.appendChild(list);
    if(newest.length > followMatch.PLAYS_SHOWN) {
      card.appendChild(followMatch.el('p', 'fw-plays__note',
        'Prikazanih je zadnjih ' + followMatch.PLAYS_SHOWN + ' od ' + newest.length + ' dogodkov.'));
    }
    $('#follow-logs').empty().append(card);
  },

  // One event. A corrected or deleted entry is NOT dropped — it stays, struck
  // through and labelled, exactly as it stays struck through on the printed
  // sheet. Only an undone entry is gone, and by then the feed has removed it.
  playRow: function(play) {
    var d = followMatch.data;
    var cancelled = play.status === 'cancelled';
    var score = play.score.home + ' : ' + play.score.away;

    if(play.kind === 'period_end') {
      var divider = followMatch.el('li', 'fw-play fw-play--quarter' + (cancelled ? ' fw-play--cancelled' : ''));
      var what = followMatch.el('span', 'fw-play__what', play.text + ' ');
      what.appendChild(followMatch.el('b', null, score));
      divider.appendChild(what);
      return divider;
    }

    var side = play.side || 'neutral';
    var row = followMatch.el('li', 'fw-play fw-play--' + side + (cancelled ? ' fw-play--cancelled' : ''));
    if(play.side) row.appendChild(followMatch.jersey(d.state.teams[play.side].color, 'fw-play__chip'));
    row.appendChild(followMatch.el('span', 'fw-play__when', play.period + ' · ' + play.minute + "'"));

    var body = followMatch.el('span', 'fw-play__what');
    if(play.player && !play.player.unknown) {
      body.appendChild(followMatch.el('b', null,
        '#' + play.player.number + ' ' + (play.player.firstName || '').charAt(0) + '. ' +
        (play.player.lastName || '').toUpperCase()));
    } else if(play.player) {
      // a log entry outlives roster membership: the player was taken off the
      // sheet after this was recorded
      body.appendChild(followMatch.el('b', 'fw-play__unknown', 'neznan igralec'));
    } else if(play.side) {
      body.appendChild(followMatch.el('b', null, d.state.teams[play.side].name || ''));
    }
    body.appendChild(document.createTextNode(' ' + play.label));
    if(cancelled) body.appendChild(followMatch.el('span', 'fw-play__mark', 'preklicano'));

    row.appendChild(body);
    row.appendChild(followMatch.el('span', 'fw-play__score', score));
    return row;
  }
}
